import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@a2z/ui";
import { useI18n } from "@/lib/i18n";
import {
  networkStore,
  useNetworkStatus,
  type NetworkStore,
} from "@/lib/api/network";
import { SystemMessage } from "./SystemMessage";

/** How long "Checking connection…" is shown at least — a probe that fails in
 *  4ms must still read as an attempt, not a button that did nothing. */
export const RETRY_FEEDBACK_MS = 1000;


/** `14:41` / `2:41 م` in the active language; `null` when nothing synced yet. */
export function formatSyncTime(at: number | null, locale: string): string | null {
  if (at === null) return null;
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(at));
  } catch {
    return null;
  }
}

/**
 * OFFLINE — inside the back-office shell (design: `system-view="offline"`).
 *
 * Opens by itself the moment `useNetworkStatus()` says the device is offline
 * (the browser's `offline` event, or an API call that failed AND a probe that
 * confirmed it) and closes by itself on reconnect.
 *
 * It is an OVERLAY on the shell's main area, never a replacement: the page
 * underneath stays mounted (the shell marks it `inert` meanwhile), so an
 * unsaved form, a half-typed note and every in-flight mutation survive the
 * outage exactly as they were — nothing is discarded to show this screen, and
 * nothing needs `UnsavedChangesPrompt`, because nothing is being left. The
 * sidebar / bottom nav are outside the overlay and stay usable.
 *
 * A11y: the pane is `role="alert"` (announced once on open); focus moves to its
 * heading, because the page it was in is now inert, and goes back to where it
 * was on reconnect; the reconnect itself is announced through a persistent
 * polite status region.
 */
export function OfflinePane({ store = networkStore }: { store?: NetworkStore }) {
  const { locale, t } = useI18n();
  const queryClient = useQueryClient();
  const { offline, lastSyncedAt } = useNetworkStatus(store);
  const [recoveryVersion, setRecoveryVersion] = useState(0);
  const [checking, setChecking] = useState(false);
  const [stillOffline, setStillOffline] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const headingRef = useRef<HTMLHeadingElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const wasOffline = useRef(false);

  useEffect(() => {
    if (offline && !wasOffline.current) {
      wasOffline.current = true;
      returnFocus.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setAnnouncement("");
      // After paint, so the heading exists to take focus.
      queueMicrotask(() => headingRef.current?.focus({ preventScroll: true }));
    } else if (!offline && wasOffline.current) {
      wasOffline.current = false;
      setStillOffline(false);
      setChecking(false);
      setAnnouncement(t((m) => m.systemScreens.offlineBackOnline));
      const target = returnFocus.current;
      returnFocus.current = null;
      if (target?.isConnected) target.focus({ preventScroll: true });
      // Whatever was read before the outage is stale; refetch what's on screen.
      void queryClient.invalidateQueries();
    }
  }, [offline, queryClient, t]);

  useEffect(() => {
    if (!offline) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    const delays = [1_000, 5_000, 15_000];
    const schedule = () => {
      if (!active || !store.getSnapshot().offline || attempt >= delays.length) return;
      timer = setTimeout(() => { void store.retry().then(schedule); }, delays[attempt++]);
    };
    schedule();
    const retryOnFocus = () => { setRecoveryVersion((value) => value + 1); void store.retry(); };
    window.addEventListener("focus", retryOnFocus);
    window.addEventListener("online", retryOnFocus);
    return () => { active = false; clearTimeout(timer); window.removeEventListener("focus", retryOnFocus); window.removeEventListener("online", retryOnFocus); };
  }, [offline, recoveryVersion, store]);

  const retry = async () => {
    if (checking) return;
    setChecking(true);
    setStillOffline(false);
    setRecoveryVersion((value) => value + 1);
    const [reachable] = await Promise.all([
      store.retry(),
      new Promise((resolve) => setTimeout(resolve, RETRY_FEEDBACK_MS)),
    ]);
    setChecking(false);
    if (!reachable) setStillOffline(true);
  };

  const time = formatSyncTime(lastSyncedAt, locale);

  return (
    <>
      <p role="status" aria-live="polite" className="sr-only" data-testid="network-announcer">
        {announcement}
      </p>
      {offline ? (
        <SystemMessage
          kind="offline"
          flow="overlay"
          testId="offline-pane"
          role="alert"
          headingRef={headingRef}
          eyebrow={
            <span data-ui="system-badge">
              <i aria-hidden="true" />
              <span>{t((m) => m.systemScreens.offlineBadge)}</span>
            </span>
          }
          title={t((m) => m.systemScreens.offlineTitle)}
          description={t((m) => m.systemScreens.offlineBody)}
          actions={
            <Button
              variant="primary"
              onClick={() => void retry()}
              disabled={checking}
              aria-busy={checking}
              data-testid="offline-retry"
            >
              {checking
                ? t((m) => m.systemScreens.offlineChecking)
                : t((m) => m.systemScreens.offlineRetry)}
            </Button>
          }
          note={
            <>
              {stillOffline ? (
                <span data-ui="system-note" role="status">
                  {t((m) => m.systemScreens.offlineStill)}
                </span>
              ) : null}
              <span data-ui="system-note">{t((m) => m.systemScreens.offlineKept)}</span>
              {time ? (
                <span data-ui="system-note" data-testid="offline-last-synced">
                  {t((m) => m.systemScreens.offlineLastSynced, { time })}
                </span>
              ) : null}
            </>
          }
        />
      ) : null}
    </>
  );
}
